import { useState } from "react";
import { AccountSurveyActionResponse } from "~/entities/actions/types";
import {
  PAYMENT_CREDIT_CARD_ANSWER_ID,
  PAYMENT_NOT_SURE_ANSWER_ID,
  PAYMENT_PURCHASE_ORDER_ANSWER_ID,
  PAYMENT_QUESTION_ID,
  SCHOOL_USER_ADMINISTRATOR_ANSWER_ID,
  SCHOOL_USER_INSTRUCTOR_ANSWER_ID,
  SCHOOL_USER_QUESTION_ID,
  SCHOOL_USER_STUDENT_FOR_SCHOOL_ANSWER_ID,
  SCHOOL_USER_STUDENT_FOR_SELF_ANSWER_ID,
} from "~/entities/survey/constants";
import useCreateAccountWizard from "~/hooks/useCreateAccountWizard";
import ErrorMessage from "./ErrorMessage";
import RadioField from "./RadioField";
import SubmitButton from "./SubmitButton";
import SurveyQuestion from "./SurveyQuestion";

const SchoolUserQuestion: React.FC<{
  checkedValue?: string;
  onChange: (value: string) => void;
}> = ({ checkedValue, onChange }) => (
  <SurveyQuestion
    ordinal={1}
    question="Which of the following are you?"
  >
    <RadioField
      id="instructor"
      name={SCHOOL_USER_QUESTION_ID}
      value={SCHOOL_USER_INSTRUCTOR_ANSWER_ID}
      checkedValue={checkedValue}
      onChange={onChange}
    >
      Instructor
    </RadioField>
    <RadioField
      id="administrator"
      name={SCHOOL_USER_QUESTION_ID}
      value={SCHOOL_USER_ADMINISTRATOR_ANSWER_ID}
      checkedValue={checkedValue}
      onChange={onChange}
    >
      Buyer or administrator
    </RadioField>
    <RadioField
      id="student_for_school"
      name={SCHOOL_USER_QUESTION_ID}
      value={SCHOOL_USER_STUDENT_FOR_SCHOOL_ANSWER_ID}
      checkedValue={checkedValue}
      onChange={onChange}
    >
      Student purchasing for a school
    </RadioField>
    <RadioField
      id="student_for_self"
      name={SCHOOL_USER_QUESTION_ID}
      value={SCHOOL_USER_STUDENT_FOR_SELF_ANSWER_ID}
      checkedValue={checkedValue}
      onChange={onChange}
    >
      Student purchasing for yourself
    </RadioField>
  </SurveyQuestion>
);

const PaymentMethodQuestion: React.FC<{ isActive: boolean }> = ({
  isActive,
}) => {
  if (!isActive) return null;
  return (
    <SurveyQuestion
      ordinal={2}
      question="How will you be paying?"
    >
      <RadioField
        id="purchase_order"
        name={PAYMENT_QUESTION_ID}
        value={PAYMENT_PURCHASE_ORDER_ANSWER_ID}
      >
        Purchase order
      </RadioField>
      <RadioField
        id="credit_card"
        name={PAYMENT_QUESTION_ID}
        value={PAYMENT_CREDIT_CARD_ANSWER_ID}
      >
        Credit card
      </RadioField>
      <RadioField
        id="not_sure"
        name={PAYMENT_QUESTION_ID}
        value={PAYMENT_NOT_SURE_ANSWER_ID}
      >
        Not sure
      </RadioField>
    </SurveyQuestion>
  );
};

type Props = {
  onSuccess: () => void;
  isActive: boolean;
};

const SchoolSurvey: React.FC<Props> = ({ onSuccess, isActive }) => {
  const { errors, fetcher } =
    useCreateAccountWizard<AccountSurveyActionResponse>(onSuccess);
  const [schoolUser, setSchoolUser] = useState<string | undefined>(undefined);

  if (!isActive) return null;

  return (
    <fetcher.Form
      method="post"
      action="/resources/create-account-wizard"
      className="flex flex-col gap-7 xsm:gap-4 md:gap-3.5 px-5"
      data-testid="school-survey-form"
    >
      <input
        type="hidden"
        name="intent"
        value="school_survey"
      />
      <h2 className="text-[20px] text-blue900 font-sans mt-0 uppercase font-bold">
        Budget-friendly prices for all.
      </h2>

      <ErrorMessage error={errors["form"]} />

      <SchoolUserQuestion
        checkedValue={schoolUser}
        onChange={(value) => setSchoolUser(value)}
      />
      <PaymentMethodQuestion
        isActive={
          schoolUser === SCHOOL_USER_INSTRUCTOR_ANSWER_ID ||
          schoolUser === SCHOOL_USER_ADMINISTRATOR_ANSWER_ID
        }
      />
      <SubmitButton
        disabled={fetcher.state === "submitting"}
        label="Almost done"
      />
    </fetcher.Form>
  );
};

export default SchoolSurvey;
